import { getCookie } from '@jouzen/ecom-utils';
import type { SegmentPlugin } from '@jouzen/onetrust-consent-manager';
import type { Context } from '@segment/analytics-next';

import { Partner } from '@/configs/partner';
import { debugAnalytics } from '@/utils/debugAnalytics';

import { getAnonymousId } from '../getAnonymousId';
import { isHipaa } from '../isHipaa';

const MASKED_IP = '0.0.0.0';
const STATSIG_STABLE_ID_COOKIE = 'statsigStableId';

/**
 * Derives the current partner from the subdomain of the hostname,
 * mirroring the logic in src/middleware.ts.
 * e.g. essence.ouraring.com -> Partner.essence
 */
function getPartner(): Partner | undefined {
  const subdomain = globalThis.location?.hostname.split('.')[0] ?? '';
  return Object.values(Partner).find((p) => p === subdomain);
}

/**
 * Enriches a Segment event with HIPAA-required fields and drops the event
 * if a unique anonymousId cannot be guaranteed.
 *
 * Replaces the full sourceMiddlewares pipeline from pre-OneTrust:
 * - withAnonymousId: sets app-scoped anonymousId from localStorage
 * - withEnhancedPayload: stamps isHipaa, ip, userId, statsigStableId
 * - withCheckStatsigId: warns when statsigStableId is missing
 * - withCheckAnonymousId: drops events where anonymousId collides with D2C cookie
 */
function enrichPayload(ctx: Context): Context {
  const event = ctx.event;
  const partner = getPartner();
  const hipaa = isHipaa(partner);

  // --- withAnonymousId + withCheckAnonymousId ---
  // getAnonymousId returns null if a unique ID cannot be generated after
  // MAX_RETRY_ATTEMPTS, meaning it still collides with the D2C Segment cookie.
  // Throw to drop the event rather than risk leaking a cross-context identifier.
  const anonymousId = getAnonymousId();
  if (!anonymousId) {
    throw new Error(
      '[hipaaPlugin] Dropping event: could not generate an anonymousId ' +
        'unique from the D2C Segment cookie after max retries.',
    );
  }
  event.anonymousId = anonymousId;

  // --- withCheckStatsigId (warn only, not blocking) ---
  const statsigStableId = getCookie(STATSIG_STABLE_ID_COOKIE);
  if (!statsigStableId) {
    debugAnalytics('hipaaPlugin: statsigStableId cookie is missing', {
      data: { partner },
      level: 'warn',
      logToDatadog: 'hipaaPlugin',
    });
  }

  // --- withEnhancedPayload ---
  // Stamp isHipaa on context.traits and properties.
  // isHipaa is true only for Partner.essence; false for all other subdomains.
  event.context = {
    ...event.context,
    ip: MASKED_IP,
    traits: {
      ...event.context?.traits,
      isHipaa: hipaa,
    },
  };

  event.properties = {
    ...event.properties,
    isHipaa: hipaa,
    statsigStableId: statsigStableId ?? undefined,
  };

  // Remove userId — eop events must be anonymous.
  event.userId = undefined;

  return ctx;
}

// Defined at module level to avoid unnecessary re-renders when passed as a prop.
export const hipaaPlugin: SegmentPlugin = {
  name: 'eop-hipaa-compliance',
  type: 'before',
  version: '1.0.0',
  isLoaded: () => true,
  load: () => Promise.resolve(),

  track: (ctx) => enrichPayload(ctx),
  page: (ctx) => enrichPayload(ctx),
  identify: (ctx) => enrichPayload(ctx),

  // --- withEventTypes ---
  // Actively block group and alias events, mirroring the old middleware behaviour.
  // In the AJS2 plugin model, event types without a handler pass through
  // unmodified, so we must explicitly throw here to drop them.
  group: () => {
    throw new Error(
      '[hipaaPlugin] Dropping event: group events are not permitted.',
    );
  },
  alias: () => {
    throw new Error(
      '[hipaaPlugin] Dropping event: alias events are not permitted.',
    );
  },
};
